import { useSeo } from "@/lib/seo";
import { ROUTES } from "@/lib/routes";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import Logo from "@/components/Logo";
import Background from "@/components/landing/Background";

export default function VerifyDocument() {
  const { t } = useT();
  useSeo({ title: "Potwierdzenie dokumentu", path: ROUTES.verifyDocument, noindex: true });
  const [params] = useSearchParams();
  const pid = params.get("doc");
  const token = params.get("t");
  const V = t.verify;

  const [state, setState] = useState("loading");
  const [to, setTo] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!pid || !token) { setState("error"); return; }
    (async () => {
      try {
        const r = await api.post(`/documents/email-confirm/${pid}`, null, { params: { t: token } });
        setTo(r.data.to || "");
        setState("ok");
      } catch {
        setState("error");
      }
    })();
  }, [pid, token]);

  return (
    <div data-testid="verify-document-page" className="min-h-screen text-foreground">
      <Background />
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Twoje Biuro — strona główna"><Logo className="h-8 text-slate-900 dark:text-white" showTagline={false} /></Link>
          <Link to={ROUTES.docs} data-testid="verify-doc-back-link" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors">
            <ArrowLeft size={13} /> {t.docs.back_list}
          </Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 text-center">
          {state === "loading" ? (
            <>
              <Loader2 data-testid="verify-doc-loading" className="animate-spin mx-auto text-slate-500" size={32} />
              <p className="text-sm text-muted-foreground mt-4">{V.verifying}</p>
            </>
          ) : state === "error" ? (
            <>
              <XCircle size={48} className="mx-auto text-rose-500" />
              <h1 data-testid="verify-doc-error-title" className="font-display font-bold text-2xl mt-5">{V.error_title}</h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{V.error_sub}</p>
            </>
          ) : (
            <>
              <CheckCircle2 size={48} className="mx-auto text-brand-teal" />
              <h1 data-testid="verify-doc-ok-title" className="font-display font-bold text-2xl mt-5">{V.doc_ok_title}</h1>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{V.doc_ok_sub.replace("{email}", to)}</p>
              <div className="mt-8 flex justify-center">
                <Link to={ROUTES.docs} className="inline-flex items-center justify-center bg-brand-teal text-white font-semibold px-8 py-3 rounded-full text-sm hover:bg-brand-tealDark transition-colors">
                  {t.docs.back_list}
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
